import React, { useEffect, useRef } from "react";
import Chart from "chart.js";
import Color from "color";
import Icon from "../../Icon/Icon";
import { format } from "../../../utils";
import { useGlobalValue } from "../../../state/GlobalProvider";
import styles from "./BalanceSheetCard.module.sass";
const TICKS_FONT_SIZE = 12;
const transform = (data, colors = [], i18n) => {
  const assets = [data.assets.currentAssets, data.assets.fixedAssets];
  const liability = [data.liability.shortTerm, data.liability.longTerm, data.liability.equity];
  const max = [data.assets.total, data.liability.total];
  const secondaryColor = new Color(colors[1]);
  const fade = secondaryColor.contrast(Color("white")) > 3 ? "lighten" : "darken";
  return {
    labels: [i18n`Assets`, i18n`Liability`],
    max,
    datasets: [{
      label: "first",
      data: [assets[0], liability[0]],
      backgroundColor: [colors[0], secondaryColor[fade](0.2).hex()],
      borderWidth: {
        top: 1
      },
      borderColor: colors[2]
    }, {
      label: "second",
      data: [assets[1], liability[1]],
      backgroundColor: [colors[0], secondaryColor[fade](0.1).hex()],
      borderWidth: {
        top: 1
      },
      borderColor: colors[2]
    }, {
      label: "third",
      data: [assets[2], liability[2]],
      backgroundColor: [colors[0], secondaryColor.hex()],
      borderWidth: {
        top: 1
      },
      borderColor: colors[2]
    }]
  };
};
const getAcidLine = (data, colors) => {
  const acidLineValue = data.assets.currentAssets - data.assets.stock;
  return {
    datasets: [{
      label: "line",
      data: [acidLineValue, acidLineValue],
      borderColor: colors[0],
      borderWidth: 2,
      // borderDash: [10, 8],
      fill: true,
      spanGaps: false,
      backgroundColor: "transparent",
      borderCapStyle: "round",
      type: "line",
      radius: 0,
      pointRadius: 0
    }]
  };
};
const renderChart = (canvas, cData, colors, i18n) => {
  let chartData = null;
  const style = getComputedStyle(canvas);
  const ctx = canvas.getContext("2d");
  chartData = cData ? transform(cData, [colors.$chartPrimaryColor, colors.$chartSecondaryColor, style.borderColor, style.stroke], i18n) : null;
  const absMax = chartData.max[0] > chartData.max[1] ? chartData.max[0] : chartData.max[1];
  return new Chart(ctx, {
    type: "bar",
    data: chartData,
    options: {
      layout: {
        padding: {
          top: 20
        }
      },
      hover: {
        mode: null,
        animationDuration: 0
      },
      tooltips: {
        enabled: false
      },
      animation: {
        duration: 0,
        onComplete: function () {
          const chartInstance = this.chart;
          const innerCtx = chartInstance.ctx;
          innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
          innerCtx.textAlign = "center";
          innerCtx.textBaseline = "bottom";
          const bars = chartInstance.controller.getDatasetMeta(1).data;
          innerCtx.fillStyle = style.color;
          bars.forEach((bar, i) => {
            const x = bar._model.x;
            const value = chartData.max[i] || 0;
            value && innerCtx.fillText(format(value, "short"), x, 14);
          });
          innerCtx.fillStyle = colors.$charTextColor;
          this.data.datasets.forEach((dataset, i) => {
            const meta = chartInstance.controller.getDatasetMeta(i);
            meta.data.forEach(function (bar, index) {
              const data = dataset.data[index];
              if (data) {
                const formatted = format(data, "short");
                innerCtx.fillText(formatted, bar._model.x, bar._model.y + (bar._model.base - bar._model.y) / 2 + 5);
              }
            });
          });
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          stacked: true,
          gridLines: {
            display: false,
            drawBorder: false
          },
          maxBarThickness: 70,
          categoryPercentage: 1.0,
          barPercentage: 0.9,
          ticks: {
            beginAtZero: true,
            fontStyle: "bold",
            fontColor: style.color
          }
        }],
        yAxes: [{
          stacked: true,
          gridLines: {
            display: true,
            drawBorder: false,
            color: style.borderColor,
            zeroLineColor: style.borderColor
          },
          ticks: {
            padding: 10,
            fontStyle: "normal",
            fontColor: style.fill,
            fontSize: TICKS_FONT_SIZE,
            callback: function (label, index, labels) {
              return index === 0 || label === 0 || index === labels.length - 1 ? format(label, "short") : null;
            }
          }
        }, {
          stacked: true,
          position: "right",
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            padding: 10,
            fontStyle: "bold",
            fontColor: "transparent",
            fontSize: TICKS_FONT_SIZE,
            callback: function (_, index) {
              return index === 0 ? format(absMax, "short") : null;
            }
          }
        }]
      },
      plugins: {
        chartJsBarChartBackgroundPlugin: {
          color: colors.$chartBackgroundColor
        }
      }
    }
    // plugins: [chartJsBarChartBackgroundPlugin]
  });
};
const renderLine = (canvas, cData, chart, colors) => {
  const style = getComputedStyle(canvas);
  const chartData = cData ? getAcidLine(cData, [colors.$charTextColor]) : null;
  const ctx = canvas.getContext("2d");
  const max = chart.scales["y-axis-0"].max;
  const min = chart.scales["y-axis-0"].min;
  return new Chart(ctx, {
    type: "line",
    data: chartData,
    options: {
      layout: {
        padding: {
          top: 20,
          left: 32,
          right: 32
        }
      },
      hover: {
        animationDuration: 0
      },
      tooltips: {
        enabled: false
      },
      animation: {
        duration: 0,
        onComplete: function () {
          const chartInstance = this.chart;
          const innerCtx = chartInstance.ctx;
          innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
          innerCtx.textAlign = "right";
          innerCtx.textBaseline = "bottom";
          innerCtx.fillStyle = colors.$charTextColor;
          const dataset = chartInstance.data.datasets[0] || {
            data: []
          };
          const points = chartInstance.controller.getDatasetMeta(0).data || [];
          const lastPoint = points[points.length - 1];
          const value = dataset.data[dataset.data.length - 1];
          if (lastPoint && value) {
            innerCtx.fillText(format(value, "short"), lastPoint._model.x, lastPoint._model.y - 8);
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          display: true,
          gridLines: {
            display: false,
            drawBorder: false
          }
        }],
        yAxes: [{
          display: false,
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            suggestedMax: max,
            suggestedMin: min
          }
        }]
      }
    }
  });
};
const renderLegend = (data, i18n) => {
  return /*#__PURE__*/React.createElement("div", {
    className: styles["legend"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["revenue"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["legend-item"]
  }, /*#__PURE__*/React.createElement("div", null, i18n`Fixed Assets`)), /*#__PURE__*/React.createElement("div", {
    className: styles["legend-item"]
  }, /*#__PURE__*/React.createElement("div", null, i18n`Current Assets`))), /*#__PURE__*/React.createElement("div", {
    className: styles["cost"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["legend-item"]
  }, /*#__PURE__*/React.createElement("div", null, i18n`Equity`)), /*#__PURE__*/React.createElement("div", {
    className: styles["legend-item"]
  }, /*#__PURE__*/React.createElement("div", null, i18n`Long Term`)), /*#__PURE__*/React.createElement("div", {
    className: styles["legend-item"]
  }, /*#__PURE__*/React.createElement("div", null, i18n`Short Term`))));
};
const BalanceSheetChartCard = props => {
  const {
    data: cData,
    title
  } = props || {};
  const {
    i18n,
    theme
  } = useGlobalValue();
  const canvas = useRef(null);
  const lineCanvas = useRef(null);
  const {
    colors = {}
  } = theme || {};
  useEffect(() => {
    const chart = canvas.current && cData && renderChart(canvas.current, cData, colors, i18n);
    const lineChart = chart && lineCanvas.current && cData && renderLine(lineCanvas.current, cData, chart, colors);
    return () => {
      chart && chart.destroy();
      lineChart && lineChart.destroy();
    };
  }, [canvas, cData, colors, i18n]);
  const legend = cData ? renderLegend(cData, i18n) : null;
  return /*#__PURE__*/React.createElement("div", {
    className: styles["root"]
  }, /*#__PURE__*/React.createElement("h5", {
    className: `widget-title ${styles["title"]}`
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Icon, {
    className: styles["icon"],
    name: "balance-sheet"
  }), /*#__PURE__*/React.createElement("div", null, title))), /*#__PURE__*/React.createElement("div", {
    className: styles["chart"]
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("canvas", {
    ref: canvas
  }), /*#__PURE__*/React.createElement("canvas", {
    ref: lineCanvas
  }))), legend);
};
export default BalanceSheetChartCard;